import React, { useEffect, useRef, useState, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import PlayerTray from '../components/PlayerTray/PlayerTray';
import SkillTreeViewport from '../components/SkillTreeViewport/SkillTreeViewport';
import SkillTreeOwnedCluster from '../components/SkillTreeViewport/SkillTreeOwnedCluster';
import PillCard from '../components/PillCard/PillCard';
import { getAccessToken, getDrugLabAnonToken, setDrugLabAnonToken, setAccessToken, clearAccessToken, clearDrugLabAnonToken } from '../utils/authStorage';
import { mintAnonDemoToken, getDrugMap, devPanelLogin } from '../api/drugLab';
import MobileDesktopBanner from '../components/MobileDesktopBanner';
import useIsMobile from '../hooks/useIsMobile';
import { foundersFor } from '../config/foundersDiscount';
import { getBossGlyph } from '../data/bossGlyphs';
import { BossGlyphGroup, bossGlyphTransform } from '../components/ProgressMap/BossGlyph';
import ROSTERS from '../data/generated/rosters.json';
import { BUILD_ROWS } from '../data/goldCardCatalog';
import './Landing.css';

// Heavy anon-demo components (combat engine + spatial map) are lazy-loaded so the
// entire BatchBossSession / ProgressMap game subtree is NOT statically bundled
// into the landing's main app.*.js (~2.75MB every debono.ai visitor downloaded +
// parsed on load -> ~8s to interactive on a throttled phone, the real cause of
// "combat takes forever / feels broken"). webpackPrefetch warms the chunk during
// idle after the marketing shell paints; both render inside <Suspense> in the
// demo frame. (Must sit BELOW all imports — eslint import/first.)
const DrugLabMap = React.lazy(() => import(/* webpackPrefetch: true */ '../components/DrugLabMap/DrugLabMap'));
const DrugLabMobileDemo = React.lazy(() => import(/* webpackPrefetch: true */ '../components/DrugLabMobileDemo/DrugLabMobileDemo'));

/* The boss node in the "Progress Map & Boss Battles" showcase draws a REAL boss.
   It used to be an invented red skull (a copy of ProgressMap's per-type FALLBACK
   mark, the one the game only reaches when a node carries no roster enemy) under
   the literal word "BOSS", which is art that exists nowhere in the game.

   DERIVED, never typed: the boss is the FIRST entry of the shipped mid-boss
   roster whose class resolves a bespoke hieroglyph through getBossGlyph() — the
   same resolver ProgressMap.jsx:562 calls, so aliased classes (lurker, phantom)
   resolve here exactly as they do in game. Position + glyph-resolution is the
   whole rule, so a roster rename, recolour, reorder or new lead boss follows
   automatically and no boss identity is written into this file. Null-safe: if
   nothing resolves, the showcase simply paints no boss mark. */
const SHOWCASE_BOSS = (() => {
  for (const boss of (ROSTERS.mid_bosses || [])) {
    const glyph = getBossGlyph({ boss_class: boss.class });
    if (glyph) return { name: boss.name, color: boss.color, glyph };
  }
  return null;
})();

/* Roster names are "Name, the Epithet" — break on the comma so the map label
   reads as two short lines instead of one that runs off a 440-wide viewBox.
   Purely typographic: no name text is authored here. */
const SHOWCASE_BOSS_LINES = SHOWCASE_BOSS
  ? SHOWCASE_BOSS.name.split(',').map((part) => part.trim()).filter(Boolean)
  : [];

/* `active` = the program that actually ships today (the demo below is a pharmacy
   deck). All three are SELECTABLE though — picking one reframes the demo headline
   ("What Debono for Medicine looks like") so a med/nursing visitor sees the
   product addressed to them; the `soon` badge stays so the pick never reads as
   "this is live". Selection is the only thing the strip drives. */
const PROGRAMS = [
  { label: 'Pharmacy', active: true },
  { label: 'Medicine', active: false },
  { label: 'Nursing', active: false },
];

/* Where each ambient cloud sits, taken by the axis's index. Spread around the
   frame and deliberately off-centre so no two are symmetric about the middle;
   the list is cycled, so more axes than spots still lands somewhere sensible
   rather than stacking. Percentages of the viewport. */
const CLOUD_SPOTS = [
  [14, 6],    // upper left
  [92, 34],   // right, above centre
  [44, 104],  // below the fold, centre-left
  [78, 92],   // lower right
  [4, 62],    // left, below centre
  [58, -6],   // above the fold, centre-right
];

const FlowIconUpload = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>;
const FlowIconPlay = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>;
const FlowIconMaster = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 010-5H6"/><path d="M18 9h1.5a2.5 2.5 0 000-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0012 0V2z"/></svg>;

const FLOW_STEPS = [
  { num: 1, title: 'Upload', desc: 'Drop your lectures, notes, or any study material. We handle the rest.', iconClass: 'landing-flow-icon-upload', icon: <FlowIconUpload />, auto: 'AUTO' },
  { num: 2, title: 'Play', desc: 'Fight bosses, collect drug cards, equip gear, and maintain your deck. The game teaches you while you play.', iconClass: 'landing-flow-icon-play', icon: <FlowIconPlay />, auto: null },
  // Outcome claims ("Ace exams. Study less. Remember more.") were promises we cannot
  // make about anyone's results. Reworded to describe what the player DOES and what
  // the design equates, both of which are true by construction.
  { num: 3, title: 'Master', desc: 'Recall it. Apply it. Defend it. The time you spend playing is time spent preparing.', iconClass: 'landing-flow-icon-master', icon: <FlowIconMaster />, auto: null },
];

const LevelIconXP = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
const LevelIconProgress = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>;
const LevelIconShield = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
const LevelIconSwords = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 17.5L3 6V3h3l11.5 11.5"/><path d="M13 19l6-6"/><path d="M16 16l4 4"/><path d="M19 21l2-2"/></svg>;
const LevelIconTrophy = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 010-5H6"/><path d="M18 9h1.5a2.5 2.5 0 000-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0012 0V2z"/></svg>;

const LEVELUP_ITEMS = [
  { icon: <LevelIconXP />, label: 'XP System', desc: 'Earn XP for every action: uploads, quizzes, battles, and more.' },
  { icon: <LevelIconProgress />, label: 'Progression', desc: 'Level up and unlock new abilities as you master material.' },
  { icon: <LevelIconShield />, label: 'Equipment', desc: 'Equip drug cards to boost your stats in combat.' },
  { icon: <LevelIconSwords />, label: 'Boss Battles', desc: 'Face boss exams that test everything you\'ve learned.' },
  { icon: <LevelIconTrophy />, label: 'Honor', desc: 'Climb the leaderboard and earn recognition.' },
];

/* Hero logo, inlined rather than loaded as <img> so the draw-on animation and the
   wordmark's write-on animation share one CSS timeline. Geometry and colour are
   identical to public/debono-logo-v6.svg — keep the two in step if either changes.
   The five paths are the whole mark: silhouette, hole, and three polylines that each
   run hole-vertex -> lap -> bend -> fold -> silhouette-vertex (120 deg rotations of
   each other). LOGO_FILL is silhouette + hole under evenodd, so the hole stays open. */
const LOGO_SILHOUETTE = 'M131.85 47.94 L168.15 47.94 L269.00 222.62 L250.85 254.06 L49.15 254.06 L31.00 222.62 Z';
const LOGO_HOLE = 'M150.00 142.27 L178.23 191.17 L121.77 191.17 Z';
const LOGO_FILL = `${LOGO_SILHOUETTE} ${LOGO_HOLE}`;
/* Written outward-in: the long fold first, from the silhouette corner, then the short
   lap tick that closes on the hole. Drawn the other way round they read as strokes
   escaping the hole rather than as detail being added to a blocked-in outline. */
const LOGO_LIMBS = [
  'M250.85 254.06 L168.15 110.83 L150.00 142.27',
  'M31.00 222.62 L196.38 222.62 L178.23 191.17',
  'M168.15 47.94 L85.46 191.17 L121.77 191.17',
];
const LOGO_STROKES = [LOGO_SILHOUETTE, LOGO_HOLE, ...LOGO_LIMBS];

/* Pen routes for the script, one per glyph, in the SVG's own uniform coordinate
   space (viewBox 550.1 x 95.4, cap band 10.68 -> 85.68). These are the path a hand
   takes THROUGH each letter, not its outline: stroked wide, they mask the real
   letterform in, so the type is untouched and only the reveal is stroke-driven.
   Derived from the measured ink box of every glyph, not guessed. */
// One entry per PEN STROKE, not per letter. SVG restarts stroke-dasharray at every
// subpath, so a path holding several subpaths cannot be drawn progressively: each
// subpath appears whole the moment the dash reaches it, which is what made the ink
// look like it was following the nib rather than being laid by it. Every stroke is
// its own path with its own window, and its centreline is measured off the rendered
// glyph (see the fit note in Landing.css).
const SCRIPT_STROKES = [
  'M8.5,6.2 L8.5,55.0', // RX 1
  'M8.5,9.8 L21.8,10.2 L27.5,13.1 L28.2,12.8 L31.3,16.6 L32.2,21.2 L32.2,28.1 L31.6,30.8 L26.7,31.0 L19.3,32.6 L8.5,32.5', // RX 2
  'M34.8,34.5 L48.8,55.0', // RX 3
  'M47.8,30.5 L28.0,55.0', // RX 4
  'M91.7,11.9 L91.7,85.6', // D 1
  'M91.7,20.6 L116.9,20.9 L122.4,22.0 L127.8,24.2 L134.1,28.8 L137.8,33.5 L140.8,40.8 L141.8,48.8 L140.8,56.7 L137.8,64.0 L134.1,68.7 L127.8,73.3 L122.4,75.4 L116.9,76.5 L91.7,76.8', // D 2
  'M212.6,20.5 L173.4,18.9 L173.4,78.6 L212.6,77.1', // E 1
  'M173.4,48.1 L215.6,48.1', // E 2
  'M241.5,85.6 L241.5,11.9', // B 1
  'M241.5,19.3 L264.1,19.3 L269.8,20.3 L273.1,22.1 L275.9,25.1 L277.7,29.1 L278.1,32.1 L277.7,35.1 L274.8,43.2 L273.0,46.5 L271.4,47.6 L266.9,48.6 L241.5,48.5', // B 2
  'M241.5,45.5 L270.4,45.9 L272.3,46.9 L275.8,51.7 L279.2,58.4 L280.2,63.5 L279.8,66.9 L277.9,71.4 L274.7,74.8 L270.9,76.9 L264.5,78.2 L241.5,78.2', // B 3
  'M340.4,19.6 L347.5,21.0 L352.0,22.8 L356.2,25.4 L359.8,28.6 L362.8,32.5 L365.9,39.1 L367.2,46.3 L367.2,51.1 L365.8,58.3 L364.0,62.8 L361.4,66.9 L358.1,70.5 L352.1,74.7 L347.6,76.5 L342.8,77.5 L335.6,77.7 L328.5,76.4 L324.0,74.6 L319.8,72.1 L316.2,68.8 L312.0,62.8 L310.1,58.3 L309.0,53.6 L308.8,46.3 L310.1,39.1 L312.0,34.6 L314.6,30.5 L317.9,26.9 L321.8,23.9 L328.5,21.0 L335.6,19.6 L340.4,19.6', // O1
  'M399.0,85.6 L399.0,11.9 L444.6,85.6 L444.6,11.9', // N
  'M508.0,19.6 L515.1,21.0 L519.6,22.8 L525.6,26.9 L529.0,30.5 L531.6,34.6 L533.4,39.1 L534.7,46.3 L534.7,51.1 L533.4,58.3 L531.6,62.8 L529.0,66.9 L525.7,70.5 L521.8,73.4 L515.1,76.4 L510.4,77.5 L503.2,77.7 L496.1,76.4 L491.5,74.6 L487.4,72.1 L483.8,68.8 L479.5,62.8 L477.7,58.3 L476.6,53.6 L476.3,46.3 L477.6,39.1 L479.5,34.6 L482.1,30.4 L485.5,26.8 L489.4,23.9 L496.1,21.0 L503.2,19.6 L508.0,19.6', // O2
];

function HeroLogo() {
  return (
    <svg
      className="landing-hero-logo"
      viewBox="19 36 262 230"
      width={396}
      height={347}
      role="img"
      aria-label="Debono"
    >
      <defs>
        <linearGradient id="dblBase" gradientUnits="userSpaceOnUse" x1="31" y1="47.94" x2="269" y2="254.06">
          <stop offset="0" stopColor="#d685ff" />
          <stop offset="0.4" stopColor="#c85aff" />
          <stop offset="0.72" stopColor="#bf40ff" />
          <stop offset="1" stopColor="#a939e2" />
        </linearGradient>
        <linearGradient id="dblSheen" gradientUnits="userSpaceOnUse" x1="0" y1="47.94" x2="0" y2="254.06">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.197" />
          <stop offset="0.5" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
        <radialGradient id="dblTop" gradientUnits="userSpaceOnUse" cx="150" cy="52" r="120">
          <stop offset="0" stopColor="#22c55e" stopOpacity="0.8" />
          <stop offset="1" stopColor="#22c55e" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="dblLeft" gradientUnits="userSpaceOnUse" cx="48" cy="242" r="120">
          <stop offset="0" stopColor="#ef4444" stopOpacity="0.62" />
          <stop offset="1" stopColor="#ef4444" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="dblRight" gradientUnits="userSpaceOnUse" cx="252" cy="242" r="120">
          <stop offset="0" stopColor="#3b82f6" stopOpacity="0.8" />
          <stop offset="1" stopColor="#3b82f6" stopOpacity="0" />
        </radialGradient>
        <clipPath id="dblClip">
          <path d={LOGO_FILL} clipRule="evenodd" />
        </clipPath>
        {/* Colour arrives on the same sweep the silhouette is drawn on. This is a circle
            centred on the mark, stroked far wider than the mark is deep, so dash-drawing
            it clockwise from the top sweeps a wedge of reveal around the centre and the
            colour lands exactly where the nib has just been. The flood is a safety net
            for the last sliver. */}
        {/* The sweep's leading edge is blurred. Unblurred it is a hard radius, and a
            hard radius crossing a thin triangular frame reveals whole bars at once,
            so the colour arrived in angular chunks that popped. Softening it turns
            the same motion into a wash. The arc also starts on the same bearing as
            the silhouette's first vertex, not at twelve o'clock; eight degrees out
            was enough to leave the top-left corner filling last despite the pen
            having drawn it first. */}
        <filter id="dblSoft" x="-25%" y="-25%" width="150%" height="150%" colorInterpolationFilters="sRGB">
          <feGaussianBlur stdDeviation="7" />
        </filter>
        <mask id="dblInk" maskUnits="userSpaceOnUse" x="19" y="36" width="262" height="230">
          <g filter="url(#dblSoft)">
            <path
              className="dbl-sweep"
              pathLength="1"
              fill="none"
              stroke="#ffffff"
              strokeWidth="260"
              d="M133.01 56.08 A120 120 0 0 1 166.99 293.66 A120 120 0 0 1 133.01 56.08"
            />
          </g>
          <path className="dbl-flood" d={LOGO_FILL} fillRule="evenodd" fill="#ffffff" />
        </mask>
      </defs>

      <g mask="url(#dblInk)">
        <path d={LOGO_FILL} fillRule="evenodd" fill="#8b3fb0" />
        <path d={LOGO_FILL} fillRule="evenodd" fill="url(#dblBase)" />
        <path d={LOGO_FILL} fillRule="evenodd" fill="url(#dblSheen)" />
        <g clipPath="url(#dblClip)">
          <rect x="19" y="36" width="262" height="230" fill="url(#dblTop)" style={{ mixBlendMode: 'hue' }} />
          <rect x="19" y="36" width="262" height="230" fill="url(#dblLeft)" style={{ mixBlendMode: 'hue' }} />
          <rect x="19" y="36" width="262" height="230" fill="url(#dblRight)" style={{ mixBlendMode: 'hue' }} />
        </g>
      </g>

      <g fill="none" stroke="rgba(10,6,14,0.62)" strokeWidth="1.15" strokeLinejoin="round" strokeLinecap="round">
        {LOGO_STROKES.map((d, i) => (
          <path key={d} className={`dbl-line dbl-seg-${i + 1}`} pathLength="1" d={d} />
        ))}
      </g>
    </svg>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user, loginWithGoogle } = useAuth();

  // Dev-only (REACT_APP_DEV_DRUGLAB_ONLY): jon's isolated dev env swaps the hero
  // CTA to a no-login "Go to Drug Lab" that mints a permanent dev session via the
  // env-gated backend, then hands off through the real _panel_token seam so the
  // REAL dashboard/Drug-Lab map mounts. Off/absent everywhere else.
  const DEV_DRUGLAB_ONLY = process.env.REACT_APP_DEV_DRUGLAB_ONLY === 'true';
  const [devEntering, setDevEntering] = useState(false);
  const goDevDrugLab = async () => {
    if (devEntering) return;
    setDevEntering(true);
    try {
      const { access_token: devToken } = await devPanelLogin();
      const base = process.env.PUBLIC_URL || '';
      window.location.assign(`${base}/dashboard?_panel_token=${encodeURIComponent(devToken)}`);
    } catch (e) {
      setDevEntering(false);
    }
  };
  // Mobile gets a dedicated, isolated Drug-Lab demo (CTA + node-picker sheet);
  // desktop keeps the spatial pick-a-node map. 768px = the demo frame's CSS breakpoint.
  const isMobile = useIsMobile(768);
  const [googleError, setGoogleError] = useState('');
  // Which program tag is picked in the hero strip — drives the demo headline.
  const [selectedProgram, setSelectedProgram] = useState(PROGRAMS[0].label);
  const [liveStats, setLiveStats] = useState({});
  const googleBtnRef = useRef(null);
  const fNaplex = foundersFor('naplex');
  const fMpje = foundersFor('mpje');
  const [showDeletedBanner, setShowDeletedBanner] = useState(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('deleted') === '1';
  });

  // ── Playable warfarin Drug-Lab demo (logged-out visitors) ──
  // 'idle' until we know whether a usable token exists; 'ready' to render the
  // map; 'error' if minting failed (we just hide the demo, page still works).
  const [demoState, setDemoState] = useState('idle');
  // Skill-tree / Drug-Lab swap on the landing, mirroring the signed-in dashboard
  // (Dashboard.jsx: skillTreeMode ? SkillTreeViewport : DrugLabMap). The anon-demo
  // user has unlock_all_golden, so the REAL equip API lets a no-signup visitor
  // equip any keystone + 3 drug cards (Edward 2026-06-08).
  const [skillTreeMode, setSkillTreeMode] = useState(false);
  // First-visit attention cue on the skill-tree orb; cleared on first click.
  const [skillTreeHintDismissed, setSkillTreeHintDismissed] = useState(false);
  const [randomLoadoutBusy, setRandomLoadoutBusy] = useState(false);
  const queryClient = useQueryClient();

  // "Random Loadout" — the anon themed roll: ONE random keystone + TWO of its
  // branch_of augments (server-side, services/skill_tree/random_loadout.py;
  // also auto-assigned at anon session creation so the preview opens
  // pre-loaded). Refresh the skill-tree + reservoir queries so the tree,
  // hotbar, and combat loadout all reflect the new equip.
  const handleRandomLoadout = async () => {
    if (randomLoadoutBusy) return;
    setRandomLoadoutBusy(true);
    try {
      await apiClient.post('/api/skill-tree/random-loadout', {});
      queryClient.invalidateQueries({ queryKey: ['player', 'skill-tree'] });
      queryClient.invalidateQueries({ queryKey: ['golden', 'reservoir'] });
    } catch (err) {
      console.error('Random loadout failed:', err);
    } finally {
      setRandomLoadoutBusy(false);
    }
  };
  useEffect(() => {
    let cancelled = false;
    const tokenValid = (jwt) => {
      if (!jwt) return false;
      try {
        const claims = JSON.parse(atob(jwt.split('.')[1] || ''));
        // expire 60s early to avoid using a token that dies mid-fetch
        return typeof claims.exp === 'number' && claims.exp * 1000 - 60000 > Date.now();
      } catch (_) {
        return false;
      }
    };
    // Genuinely logged-in (VALID, non-expired access token) → use it, no mint.
    if (tokenValid(getAccessToken())) { setDemoState('ready'); return undefined; }
    // From here: no usable token. A stale/EXPIRED access token may still sit in
    // storage (e.g. an earlier login that timed out). It must not be trusted —
    // on a public page the 401 interceptor short-circuits (no refresh, no
    // clear), so an expired access token poisons getAuthToken() precedence
    // (access > anon) and silently 401s every demo map fetch. Fix: one-shot
    // refresh in case a live refresh cookie can revive the real session;
    // otherwise drop the dead token so the freshly-minted anon token wins.
    (async () => {
      // A reusable anon token may be DEMO-SCOPED (from playing /play/<slug>): the
      // backend pins that session to one rxcui, so it 404s the warfarin preview
      // map and the landing renders "Could not build a map." Only reuse it if it
      // can actually build the preview; otherwise drop it and mint a fresh
      // (unscoped) anon token below. This also heals a token already poisoned by
      // an earlier demo visit (the token persists in storage across reloads).
      const anonToken = getDrugLabAnonToken();
      if (tokenValid(anonToken)) {
        try {
          await getDrugMap('11289'); // warfarin — the pinnedRxcui landing preview
          if (cancelled) return;
          setDemoState('ready');
          return;
        } catch (_) {
          if (cancelled) return;
          clearDrugLabAnonToken();
        }
      }
      const staleAccess = getAccessToken();
      if (staleAccess) {
        try {
          const r = await apiClient.post('/api/auth/refresh', null, {
            withCredentials: true,
            _isRefreshAttempt: true,
            _skipAuthRedirect: true,
          });
          if (cancelled) return;
          if (r?.data?.access_token) {
            setAccessToken(r.data.access_token);
            setDemoState('ready');
            return;
          }
        } catch (_) {
          // dead session — fall through to clear + anon
        }
        if (cancelled) return;
        clearAccessToken();
      }
      try {
        const data = await mintAnonDemoToken();
        if (cancelled) return;
        if (data?.access_token) {
          setDrugLabAnonToken(data.access_token);
          setDemoState('ready');
        } else {
          setDemoState('error');
        }
      } catch (_) {
        if (!cancelled) setDemoState('error');
      }
    })();
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    if (!showDeletedBanner) return undefined;
    const t = setTimeout(() => setShowDeletedBanner(false), 7000);
    return () => clearTimeout(t);
  }, [showDeletedBanner]);

  // Fetch live platform stats
  useEffect(() => {
    apiClient.get('/api/site-stats')
      .then(res => setLiveStats(res.data))
      .catch(() => {});
  }, []);

  // Logged-in users can browse the marketing homepage (Edward 2026-06-03) —
  // CTAs below switch to "Go to Dashboard" instead of redirecting away, so the
  // page stays readable/SEO-able for everyone. (Was: redirect authed → dashboard.)

  // Stable refs so the Google init effect runs once on mount (deps=[]). See
  // matching note on Login.jsx — re-running google.accounts.id.initialize()
  // aborts the in-flight GSI button iframe and breaks the visual button.
  const loginWithGoogleRef = useRef(loginWithGoogle);
  const navigateRef = useRef(navigate);
  useEffect(() => { loginWithGoogleRef.current = loginWithGoogle; }, [loginWithGoogle]);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);

  // Google Sign-In initialization
  useEffect(() => {
    let cancelled = false;
    let tries = 0;
    const initGoogle = async () => {
      try {
        const response = await apiClient.get('/api/auth/google/client-id');
        const clientId = response.data.client_id;
        if (cancelled || !clientId) return;

        const waitForGoogle = () => {
          if (window.google?.accounts?.id) {
            window.google.accounts.id.initialize({
              client_id: clientId,
              callback: async (resp) => {
                const credential = resp?.credential;
                if (credential) {
                  const result = await loginWithGoogleRef.current(credential);
                  if (result.success) {
                    navigateRef.current('/dashboard');
                  } else {
                    setGoogleError(result.error || 'Google sign-in failed');
                  }
                }
              },
            });
            if (googleBtnRef.current) {
              window.google.accounts.id.renderButton(googleBtnRef.current, {
                theme: 'filled_black',
                size: 'large',
                shape: 'pill',
                text: 'continue_with',
                width: 280,
              });
            }
          } else {
            tries += 1;
            if (tries < 30) {
              setTimeout(waitForGoogle, 200);
            }
          }
        };
        waitForGoogle();
      } catch {
        // Google Sign-In not available
      }
    };
    initGoogle();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('landing-visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    const els = document.querySelectorAll('.landing-animate');
    els.forEach((el) => observer.observe(el));
    return () => els.forEach((el) => observer.unobserve(el));
  }, []);

  // ---- Background lighting -------------------------------------------------
  //
  // The ambient colour states which WAY OF KNOWING is currently on screen. It
  // is driven ENTIRELY by what the page contains, so adding, moving, removing
  // or renaming content never requires touching this code:
  //
  //   * the set of clouds is DISCOVERED from the distinct data-axis values
  //     present in the DOM — it is not a list kept here. A new axis needs one
  //     --axis-<name> colour token in Landing.css and nothing else; a cloud for
  //     it appears on its own.
  //   * a MutationObserver re-discovers on any DOM change, so content that
  //     mounts late (lazy sections, conditionals, a feature flag) is picked up
  //     without a reload.
  //   * an axis's weight is how close its NEAREST tagged element is to the
  //     viewport centre, on a linear kernel one viewport tall. Any element can
  //     carry data-axis — a card, a whole section, a single figure.
  //
  // So the maintenance contract is: put data-axis on the thing, define its
  // colour token if the axis is new. That is the whole interface.
  const landingRef = useRef(null);
  const [axes, setAxes] = useState([]);
  const cloudRefs = useRef(new Map());

  // Discover the axes the page actually uses, in first-appearance order (stable
  // as content is added below, unlike sorting, which would renumber the lot).
  useEffect(() => {
    const el = landingRef.current;
    if (!el) return undefined;

    const read = () => {
      const seen = [];
      el.querySelectorAll('[data-axis]').forEach((node) => {
        const axis = node.getAttribute('data-axis');
        if (axis && !seen.includes(axis)) seen.push(axis);
      });
      setAxes((prev) => (
        prev.length === seen.length && prev.every((a, i) => a === seen[i]) ? prev : seen
      ));
    };

    read();
    const mo = new MutationObserver(read);
    mo.observe(el, { childList: true, subtree: true, attributeFilter: ['data-axis'] });
    return () => mo.disconnect();
  }, []);

  useEffect(() => {
    const el = landingRef.current;
    if (!el) return undefined;

    const FLOOR = 0.3;   // an unlit axis still tints; the page is never 2-colour
    const REST = 0.62;   // the hero, where no mechanic is on screen yet

    // Tagged elements are cached because this runs on every scroll frame, and a
    // querySelectorAll per frame is the cost that only shows up on a slow
    // machine. Re-collected whenever the DOM or the layout changes.
    let tagged = [];
    const collect = () => {
      tagged = Array.from(el.querySelectorAll('[data-axis]')).map((node) => {
        const axis = node.getAttribute('data-axis');
        // Resolve the element's own colour from its axis NAME. Doing it here
        // rather than as one CSS rule per axis is what stops the stylesheet
        // carrying a list that has to be kept in step with the markup — the
        // colour token is then the single place an axis is declared anywhere.
        // (Safe against the observers below: they filter on data-axis, so a
        // style write cannot re-trigger them.)
        node.style.setProperty('--axis', `var(--axis-${axis}, var(--axis-fallback))`);
        return { node, axis };
      });
    };

    let raf = 0;
    const update = () => {
      raf = 0;
      const vh = window.innerHeight || 1;
      const mid = vh / 2;
      const weights = new Map();
      let anyNear = false;

      tagged.forEach(({ node, axis }) => {
        const r = node.getBoundingClientRect();
        // A detached or display:none element reports an all-zero rect, which
        // sits at the top of the document and would otherwise score ~0.5 — a
        // phantom weight for content that is not on the page at all. Skip it.
        if (r.width === 0 && r.height === 0) return;
        // distance from the viewport centre to the element's nearest edge, so a
        // tall element counts as "on screen" for the whole time it is on screen
        const d = Math.max(0, Math.max(r.top - mid, mid - r.bottom));
        const w = Math.max(0, 1 - d / vh);
        if (w > 0) anyNear = true;
        if (w > (weights.get(axis) || 0)) weights.set(axis, w);
      });

      cloudRefs.current.forEach((node, axis) => {
        if (!node) return;
        // Before any tagged element is near, sit at the resting mix rather than
        // the floor: the hero is about all the axes at once, not about none.
        const w = anyNear ? Math.max(FLOOR, weights.get(axis) || 0) : REST;
        node.style.setProperty('--w', w.toFixed(3));
      });
    };

    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    const onLayout = () => { collect(); onScroll(); };

    collect();
    update();

    // Same observer contract as the discovery pass: late-mounting content is
    // measured without a reload.
    const mo = new MutationObserver(onLayout);
    mo.observe(el, { childList: true, subtree: true, attributeFilter: ['data-axis'] });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onLayout, { passive: true });
    return () => {
      mo.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onLayout);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [axes]);

  return (
    <div className="landing" ref={landingRef}>
      <Helmet>
        <title>Debono: AI Pharmacy RPG | NAPLEX Prep &amp; Gamified Study</title>
        <meta name="description" content="Upload your pharmacy lectures and get AI-generated flashcards, quizzes, boss fights, and adaptive NAPLEX study paths built from your own material." />
        <link rel="canonical" href="https://debono.ai/" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Debono: AI Pharmacy RPG | NAPLEX Prep &amp; Gamified Study" />
        <meta property="og:description" content="Upload your pharmacy lectures and get AI-generated flashcards, quizzes, boss fights, and adaptive NAPLEX study paths built from your own material." />
        <meta property="og:url" content="https://debono.ai/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Debono RX" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Debono: AI Pharmacy RPG | NAPLEX Prep &amp; Gamified Study" />
        <meta name="twitter:description" content="Upload your pharmacy lectures and get AI-generated flashcards, quizzes, boss fights, and adaptive NAPLEX study paths built from your own material." />
      </Helmet>
      {/* Ambient background. One cloud per axis the page actually uses, so this
          layer is a function of the content and never a list to maintain by
          hand — see the discovery effect above and .landing-cloud in the CSS.
          Placement comes from the axis's index, so the clouds stay spread out
          however many there turn out to be. */}
      <div className="landing-bg">
        {axes.map((axis, i) => (
          <span
            key={axis}
            className="landing-cloud"
            data-cloud={axis}
            ref={(node) => {
              if (node) cloudRefs.current.set(axis, node);
              else cloudRefs.current.delete(axis);
            }}
            style={{
              '--axis': `var(--axis-${axis}, var(--axis-fallback))`,
              '--cx': `${CLOUD_SPOTS[i % CLOUD_SPOTS.length][0]}%`,
              '--cy': `${CLOUD_SPOTS[i % CLOUD_SPOTS.length][1]}%`,
            }}
          />
        ))}
      </div>

      {showDeletedBanner && (
        <div className="landing-deleted-banner" role="status" aria-live="polite">
          <span className="landing-deleted-banner-icon" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
          <span className="landing-deleted-banner-text">Account deleted. All your data has been permanently removed.</span>
          <button
            type="button"
            className="landing-deleted-banner-close"
            aria-label="Dismiss"
            onClick={() => setShowDeletedBanner(false)}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* ============ HERO ============ */}
      <section className="landing-hero">
        <div className="landing-hero-inner">
          <HeroLogo />

          <h1 className="landing-hero-name" aria-label="Debono">
            <svg className="landing-hero-script" viewBox="0 0 550.1 95.4" aria-hidden="true" focusable="false">
              <defs>
                <linearGradient id="dbScriptFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="#ffffff" />
                  <stop offset="0.55" stopColor="#f4ecfb" />
                  <stop offset="1" stopColor="#d9c2f0" />
                </linearGradient>
                <mask id="dbScriptInk" maskUnits="userSpaceOnUse" x="-60" y="-60" width="670" height="216">
                  <g fill="none" stroke="#ffffff" strokeWidth="26" strokeLinecap="round" strokeLinejoin="round">
                    {SCRIPT_STROKES.map((d, i) => (
                      <path key={d} className={`dbs-ink dbs-st-${i + 1}`} pathLength="1" d={d} />
                    ))}
                  </g>
                </mask>
              </defs>
              <g mask="url(#dbScriptInk)" fill="url(#dbScriptFill)">
                <text className="landing-hero-script-rx" x="0" y="54.08">&#8478;</text>
                <text className="landing-hero-script-word" x="75.2" y="85.68">DEBONO</text>
              </g>
              {/* The nib. One per route, replaying sampled positions along that pen
                  route, so it is genuinely where the ink is being laid. It lifts
                  (fades out) where a route jumps between strokes. Sits outside the
                  mask so it is never clipped. */}
              <g className="dbs-nibs">
                {SCRIPT_STROKES.map((d, i) => (
                  <rect key={d} className={`dbs-nib dbs-st-${i + 1}`} x="-13" y="-4" width="26" height="8" rx="4" />
                ))}
              </g>
            </svg>
          </h1>

          <p className="landing-hero-tagline">
            <span>Gamified</span>
            <span>Healthcare</span>
            <span className="landing-hero-tagline-glow">Education</span>
          </p>

          <p className="landing-hero-sub">
            {/* The emphasis states the ACTOR and the PROMISE-FREE differentiator.
                The four artifacts are deliberately plain: they were carrying the
                serif <em> before, which spent the sentence's emphasis on a list. */}
            Upload your lectures. <em>We build</em> drug cards, concept maps, quizzes, and boss battles, all gamified and <em>personalized to how you learn</em>.
          </p>

          <div className="landing-hero-ctas">
            {DEV_DRUGLAB_ONLY ? (
              <button type="button" className="landing-btn-primary" style={{ '--btn-bg': '#8b5cf6' }} onClick={goDevDrugLab} disabled={devEntering}>
                {devEntering ? 'Entering...' : 'Go to Drug Lab'}
              </button>
            ) : user ? (
              <Link to="/dashboard" className="landing-btn-primary" style={{ '--btn-bg': '#8b5cf6' }}>Go to Dashboard</Link>
            ) : (
              <>
                <Link to="/signup" className="landing-btn-primary" style={{ '--btn-bg': '#8b5cf6' }}>Get Started Free</Link>
                <Link to="/login" className="landing-btn-ghost">Log In</Link>
              </>
            )}
            {/* DEBONO WIKI — sits in the row in every auth state (it is a public
                reference, not an account action). Route is /compendium, registered
                in App.jsx and in utils/publicPaths.js so a logged-out visitor is not
                bounced to /login. <Link>, never <a href>: the app is served under
                PUBLIC_URL=/debono and only the router applies that basename.
                THE DOOR AND THE ROOM ARE NAMED DIFFERENTLY ON PURPOSE (Jon,
                2026-08-11). This label carried the page's own name through two
                renames — "Debono Compendium", then "Apothecarius’ Compendium" on
                2026-08-10 — on the reasoning that a rename leaving the old name on
                the front door is two names for one thing. That is now reversed by
                decision: the FRONT DOOR says what the thing is to someone who has
                never opened it, and APOTHECARIUS’ COMPENDIUM is what it calls
                itself INSIDE (the <h1> and document.title at Compendium.jsx:1450,
                1534-1536, both unchanged). Do not "fix" this back into agreement
                with the page title.
                Casing is the row's idiom, not this string's: `.landing-btn-ghost`
                sets `text-transform: uppercase` (Landing.css:1192), so this paints
                DEBONO WIKI exactly as its two siblings paint from title case.
                THE SHORTER LABEL UN-WRAPPED THE ROW, measured: the button is 150.8px
                (was 276), so the three now total 517.2 and sit on ONE line at 1600 /
                1440 / 1280 / 1024 / 768, where 1440 and 1280 used to run to two lines
                and 124px of row height. The old comment here argued the wrap read as
                the right hierarchy — two account actions above, the public reference
                below. There is no wrap left to justify. 390 still wraps to two lines
                and always did: GET STARTED FREE + LOG IN alone is 342.3 in a 358px
                row. */}
            <Link to="/compendium" className="landing-btn-ghost landing-btn-compendium">
              Debono Wiki
            </Link>
          </div>

          {/* Google Sign-In */}
          <div className="landing-google-divider">
            <span>or</span>
          </div>

          {/* Program selector — moved below the "or" line */}
          <div className="landing-program-strip">
            {PROGRAMS.map((p) => (
              <button
                type="button"
                key={p.label}
                aria-pressed={selectedProgram === p.label}
                onClick={() => setSelectedProgram(p.label)}
                className={`landing-program-tag ${selectedProgram === p.label ? 'landing-program-tag-active' : 'landing-program-tag-idle'}`}
              >
                {p.label}
                {!p.active && <span className="landing-program-tag-soon">soon</span>}
              </button>
            ))}
          </div>

          <div className="landing-google-btn" ref={googleBtnRef} />
          {googleError && <p className="landing-google-error">{googleError}</p>}
        </div>
      </section>

      {/* ============ PLAYABLE WARFARIN DRUG-LAB — no-signup combat preview ============
          Its own full-viewport section. The frame carries `deck-map-area` so
          BossMapModal's portal target (querySelector('.deck-map-area')) lands HERE —
          combat renders confined to the map stage exactly like the signed-in
          dashboard, via the real BatchBossSession path (no bottom-left rail). */}
      {demoState === 'ready' && (
        <section className="landing-demo-section">
          <h2 className="landing-druglab-stage-title">What Debono for {selectedProgram} looks like</h2>
          <p className="landing-druglab-stage-label">Play it now, no signup. Pick a node, fight the enemy.</p>
          {/* Real dashboard map-area chrome, reused 1:1 (Dashboard.jsx:6146-6186):
              the AchievementsOrb skill-tree toggle (PlayerTray skillTreeOnly, nav
              gated), the SkillTreeOwnedCluster equip hotbar, and the
              SkillTreeViewport / DrugLabMap swap — all driven by the anon user's
              real server player-state + real equip API. The anon session arrives
              pre-loaded (random keystone + 2 of its augments, assigned at
              creation); this manual tree is the redundant hands-on path. */}
          <div className="landing-druglab-stage-frame deck-map-area">
            <Suspense fallback={<div className="landing-druglab-demo-loading" aria-busy="true" style={{ width: '100%', minHeight: '60vh' }} />}>
            {isMobile ? (
              /* Isolated mobile path: no spatial map, no shared ProgressMap, no
                 equip hotbar chrome — just the CTA + node-picker → real combat. */
              <DrugLabMobileDemo rxcui="11289" />
            ) : (
              <>
                {skillTreeMode ? (
                  <SkillTreeViewport onExit={() => setSkillTreeMode(false)} />
                ) : (
                  <DrugLabMap pinnedRxcui="11289" preview />
                )}
                <PlayerTray
                  skillTreeOnly
                  skillTreeActive={skillTreeMode}
                  skillTreeHint={!skillTreeHintDismissed}
                  onSkillTree={() => {
                    setSkillTreeHintDismissed(true);
                    setSkillTreeMode((prev) => !prev);
                  }}
                  onRandomLoadout={handleRandomLoadout}
                  randomLoadoutBusy={randomLoadoutBusy}
                />
                <SkillTreeOwnedCluster />
              </>
            )}
            </Suspense>
          </div>
          <div className="landing-demo-cta">
            <Link to="/signup" className="landing-btn-primary" style={{ '--btn-bg': '#3b82f6' }}>Get Started Free</Link>
            <Link to="/login" className="landing-btn-secondary">Log In</Link>
          </div>
        </section>
      )}

      {/* ============ UPLOAD TO MASTERY — 3-step flow ============ */}
      <section className="landing-flow">
        <div className="landing-section-inner">
          <p className="landing-section-label landing-animate">Upload to Mastery</p>
          <p className="landing-flow-subtitle landing-animate">Your material. Three steps. Total command.</p>
          <div className="landing-flow-grid">
            {FLOW_STEPS.map((step, i) => (
              <React.Fragment key={step.num}>
                {i > 0 && (
                  <div className="landing-flow-arrow landing-animate">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M7 4l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                )}
                <div className="landing-flow-card landing-animate">
                  {step.auto && (
                    <span className={`landing-flow-auto-badge${step.auto === 'AUTO + YOU' ? ' landing-flow-auto-badge-mixed' : ''}`}>
                      {step.auto}
                    </span>
                  )}
                  <div className="landing-flow-step-num">{step.num}</div>
                  <div className={`landing-flow-icon ${step.iconClass}`}>
                    {step.icon}
                  </div>
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      <p className="landing-sentiment landing-animate">You'll forget you were studying.</p>

      {/* ============ WHAT WE GIVE YOU — Feature showcases ============ */}
      <section className="landing-features">
        <div className="landing-section-inner">
          <p className="landing-section-label landing-animate">What We Give You</p>

          {/* The study-features / gamification split is gone: they were never two
              lists, they were the same list described twice. Each showcase now
              leads with the LEARNING METHOD it implements, then shows the game
              mechanic that carries it. */}
          <p className="landing-features-lede landing-animate">
            Every mechanic here is a study method wearing a costume. The label is the method; the feature is how the game runs it.
          </p>

          <p className="landing-sentiment landing-animate">Train like an expert while having fun.</p>

          {/* Mastery-gated progression → Progress Map & Boss Battles */}
          <div className="landing-showcase landing-animate" data-axis="understanding">
            <div className="landing-showcase-text">
              <span className="landing-method-tag">Mastery-gated progression</span>
              <h3>Progress Map & Boss Battles</h3>
              <p>You move on when you can prove it, not when you've read it. Your material becomes a visual map where every node is a challenge — trials, content reviews, and boss battles that test your mastery before the next node opens.</p>
            </div>
            <div className="landing-showcase-visual">
              <svg className="landing-map-svg" viewBox="0 0 440 260" fill="none">
                <defs>
                  <pattern id="mapDots" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                    <circle cx="10" cy="10" r="0.5" fill="rgba(255,255,255,0.04)" />
                  </pattern>
                  <filter id="greenGlow" x="-60%" y="-60%" width="220%" height="220%">
                    <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur" />
                    <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                  </filter>
                  <filter id="purpleGlow" x="-60%" y="-60%" width="220%" height="220%">
                    <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
                    <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                  </filter>
                </defs>
                <rect width="440" height="260" fill="url(#mapDots)" />
                <path d="M62,70 C90,62 105,48 130,52" stroke="rgba(34,197,94,0.5)" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M148,58 C170,68 175,92 168,112" stroke="rgba(34,197,94,0.5)" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M178,122 C200,130 218,138 232,150" stroke="rgba(167,139,250,0.4)" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M148,50 C180,30 210,28 235,45" stroke="rgba(245,158,11,0.35)" strokeWidth="1.2" strokeLinecap="round" strokeDasharray="3 2" />
                <path d="M252,56 C265,72 260,100 248,140" stroke="rgba(167,139,250,0.3)" strokeWidth="1.2" strokeLinecap="round" strokeDasharray="3 2" />
                <path d="M255,162 C280,175 300,182 318,178" stroke="rgba(59,130,246,0.35)" strokeWidth="1.2" strokeLinecap="round" strokeDasharray="3 2" />
                {/* Approach edge to the boss node — takes the boss's own roster colour
                    so the path reads as leading to THAT fight, not to a generic red one.
                    Gated on the same condition as the node it leads to: with no boss
                    resolved there is nothing at the end of it, and a hardcoded red
                    fallback would put back exactly the invented mark this replaced. */}
                {SHOWCASE_BOSS && (
                  <path d="M340,170 C358,158 370,138 378,118" stroke={SHOWCASE_BOSS.color} strokeOpacity="0.3" strokeWidth="1.5" strokeLinecap="round" />
                )}
                <path d="M55,78 C48,100 50,130 68,152" stroke="rgba(59,130,246,0.25)" strokeWidth="1" strokeLinecap="round" strokeDasharray="3 2" />
                <g filter="url(#greenGlow)">
                  <circle cx="50" cy="65" r="14" fill="rgba(34,197,94,0.15)" stroke="#22c55e" strokeWidth="1.5" />
                  <circle cx="50" cy="65" r="7" fill="#22c55e" opacity="0.2" />
                  <path d="M45,65 L48,69 L55,61" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </g>
                <text x="50" y="88" textAnchor="middle" fill="rgba(160,160,160,0.7)" fontSize="8" fontWeight="600" fontFamily="var(--font-primary, sans-serif)">Intro</text>
                <g filter="url(#greenGlow)">
                  <circle cx="140" cy="48" r="14" fill="rgba(34,197,94,0.15)" stroke="#22c55e" strokeWidth="1.5" />
                  <circle cx="140" cy="48" r="7" fill="#22c55e" opacity="0.2" />
                  <path d="M135,48 L138,52 L145,44" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </g>
                <text x="140" y="71" textAnchor="middle" fill="rgba(160,160,160,0.7)" fontSize="8" fontWeight="600" fontFamily="var(--font-primary, sans-serif)">ACE-I</text>
                <g filter="url(#greenGlow)">
                  <circle cx="165" cy="118" r="12" fill="rgba(34,197,94,0.15)" stroke="#22c55e" strokeWidth="1.5" />
                  <circle cx="165" cy="118" r="6" fill="#22c55e" opacity="0.2" />
                  <path d="M160,118 L163,122 L170,114" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </g>
                <g opacity="0.85">
                  <rect x="-9" y="-9" width="18" height="18" rx="1" transform="translate(245,48) rotate(45)" fill="rgba(245,158,11,0.12)" stroke="#f59e0b" strokeWidth="1.5" />
                  <g transform="translate(240,43)"><path d="M1,1.5h3a2,2 0 012,2V11a1.5,1.5 0 00-1.5-1.5H1z" fill="none" stroke="#f59e0b" strokeWidth="1"/><path d="M9,1.5H6a2,2 0 00-2,2V11a1.5,1.5 0 011.5-1.5H9z" fill="none" stroke="#f59e0b" strokeWidth="1"/></g>
                </g>
                <text x="245" y="73" textAnchor="middle" fill="rgba(160,160,160,0.5)" fontSize="7" fontWeight="600" fontFamily="var(--font-primary, sans-serif)">MOA</text>
                <g filter="url(#purpleGlow)">
                  <circle cx="242" cy="155" r="14" fill="rgba(167,139,250,0.15)" stroke="#a78bfa" strokeWidth="1.5" />
                  <polygon points="0,-6 1.8,-2 6,-2 2.7,1.2 4,5.5 0,3 -4,5.5 -2.7,1.2 -6,-2 -1.8,-2" transform="translate(242,155)" fill="none" stroke="#a78bfa" strokeWidth="1" opacity="0.7" />
                </g>
                <text x="242" y="178" textAnchor="middle" fill="rgba(160,160,160,0.6)" fontSize="8" fontWeight="600" fontFamily="var(--font-primary, sans-serif)">Trial</text>
                <g opacity="0.8">
                  <circle cx="328" cy="175" r="12" fill="rgba(59,130,246,0.12)" stroke="#3b82f6" strokeWidth="1.5" />
                  <clipPath id="fountainClip"><circle cx="328" cy="175" r="11" /></clipPath>
                  <rect x="317" y="178" width="22" height="12" fill="rgba(59,130,246,0.25)" clipPath="url(#fountainClip)" />
                  <path d="M328,170 C328,170 324,175 324,177 C324,179.2 325.8,181 328,181 C330.2,181 332,179.2 332,177 C332,175 328,170 328,170Z" fill="none" stroke="#3b82f6" strokeWidth="1" opacity="0.6" />
                </g>
                <g opacity="0.5">
                  <circle cx="72" cy="160" r="11" fill="rgba(59,130,246,0.12)" stroke="#3b82f6" strokeWidth="1.2" />
                  <text x="72" y="164" textAnchor="middle" fill="#3b82f6" fontSize="9" fontWeight="600">?</text>
                </g>
                <g opacity="0.25">
                  <circle cx="310" cy="225" r="11" fill="rgba(239,68,68,0.1)" stroke="rgba(239,68,68,0.4)" strokeWidth="1.2" />
                </g>
                {/* Real boss node: SHOWCASE_BOSS's own hieroglyph, in its own roster
                    colour, under its own roster name.
                    It used to hand-copy the game's <g> and said so ("mirrors the game's
                    call site exactly"). It now MOUNTS that call site: BossGlyphGroup
                    was extracted from ProgressMap.jsx on 2026-08-09 precisely because
                    this comment was describing a second hand copy, and a third was
                    about to be written for the compendium. Same marks, same 24x24 box,
                    same weight 1.8, same r-relative transform with r=20 — proven by
                    capturing this node's rendered outerHTML before and after. */}
                {SHOWCASE_BOSS && (
                  <>
                    <g transform="translate(382,100)">
                      <circle r="23" fill="none" stroke={SHOWCASE_BOSS.color} strokeWidth="1" opacity="0.4" />
                      <circle r="20" fill={SHOWCASE_BOSS.color} fillOpacity="0.16" stroke={SHOWCASE_BOSS.color} strokeWidth="2" />
                      <BossGlyphGroup
                        glyph={SHOWCASE_BOSS.glyph}
                        transform={bossGlyphTransform(20)}
                        stroke={SHOWCASE_BOSS.color}
                        style={{ color: SHOWCASE_BOSS.color }}
                      />
                    </g>
                    {SHOWCASE_BOSS_LINES.map((line, i) => (
                      <text
                        key={line}
                        x="382"
                        y={132 + i * 10}
                        textAnchor="middle"
                        fill={SHOWCASE_BOSS.color}
                        fillOpacity={i === 0 ? 0.85 : 0.6}
                        fontSize={i === 0 ? 9 : 8}
                        fontWeight={i === 0 ? 700 : 600}
                        fontFamily="var(--font-primary, sans-serif)"
                      >
                        {line}
                      </text>
                    ))}
                  </>
                )}
              </svg>
            </div>
          </div>

          {/* Active recall → Concept Cards & Drills */}
          <div className="landing-showcase landing-showcase-reverse landing-animate" data-axis="knowledge">
            <div className="landing-showcase-text">
              <span className="landing-method-tag">Active recall</span>
              <h3>Concept Cards & Drills</h3>
              <p>Pulling an answer out of your head beats reading it again. Key concepts are extracted into cards with comparison tables and mastery tracking, then handed back as drills that make you produce the answer instead of recognizing it.</p>
            </div>
            <div className="landing-showcase-visual">
              <div className="landing-mock-concept">
                <div className="landing-mock-concept-header">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                  ACE Inhibitors vs ARBs
                </div>
                <table className="landing-mock-concept-table">
                  <thead>
                    <tr><th></th><th>ACE Inhibitors</th><th>ARBs</th></tr>
                  </thead>
                  <tbody>
                    <tr><td>Target</td><td>ACE enzyme</td><td>AT1 receptor</td></tr>
                    <tr><td>Cough</td><td>Common (bradykinin)</td><td>Rare</td></tr>
                    <tr><td>Prototype</td><td>Lisinopril</td><td>Losartan</td></tr>
                  </tbody>
                </table>
                <div className="landing-mock-concept-mastery">
                  <div className="landing-mock-concept-bar"><div className="landing-mock-concept-fill" /></div>
                  <span>72%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Desirable difficulty → Adaptive Quizzes */}
          <div className="landing-showcase landing-animate" data-axis="knowledge">
            <div className="landing-showcase-text">
              <span className="landing-method-tag">Desirable difficulty</span>
              <h3>Adaptive Quizzes</h3>
              <p>Practice only pays when it sits just past what you already know. Quizzes are generated from your own content and weighted toward what you need to review most, so the questions keep landing on the weak spots instead of the easy wins.</p>
            </div>
            <div className="landing-showcase-visual">
              <div className="landing-mock-quiz">
                <p className="landing-mock-quiz-q">Which adverse effect is most associated with ACE inhibitors?</p>
                <div className="landing-mock-quiz-options">
                  <div className="landing-mock-quiz-opt">
                    <span className="landing-mock-quiz-radio" />
                    Hypokalemia
                  </div>
                  <div className="landing-mock-quiz-opt landing-mock-quiz-opt-correct">
                    <span className="landing-mock-quiz-radio landing-mock-quiz-radio-correct" />
                    Dry cough
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <div className="landing-mock-quiz-opt">
                    <span className="landing-mock-quiz-radio" />
                    Peripheral edema
                  </div>
                  <div className="landing-mock-quiz-opt">
                    <span className="landing-mock-quiz-radio" />
                    Bradycardia
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Retrieval under pressure → PvP Combat */}
          <div className="landing-showcase landing-showcase-reverse landing-animate" data-axis="intuition">
            <div className="landing-showcase-text">
              <span className="landing-method-tag">Retrieval under pressure</span>
              <h3>PvP Combat</h3>
              <p>Exam recall is timed recall — knowing it slowly isn't the same skill. Challenge classmates to real-time drug knowledge battles: equip your best cards, answer fast, and climb the rankings.</p>
            </div>
            <div className="landing-showcase-visual">
              <div className="landing-mock-pvp">
                <div className="landing-mock-pvp-side">
                  <div className="landing-mock-pvp-header">
                    <span className="landing-mock-pvp-name landing-mock-pvp-name-player">YOU</span>
                    <span className="landing-mock-pvp-hp-value">72/100</span>
                  </div>
                  <div className="landing-mock-pvp-track">
                    <div className="landing-mock-pvp-fill landing-mock-pvp-fill-player" style={{ width: '72%' }} />
                  </div>
                </div>
                <div className="landing-mock-pvp-vs">VS</div>
                <div className="landing-mock-pvp-side">
                  <div className="landing-mock-pvp-header">
                    <span className="landing-mock-pvp-name landing-mock-pvp-name-opponent">RIVAL</span>
                    <span className="landing-mock-pvp-hp-value">45/100</span>
                  </div>
                  <div className="landing-mock-pvp-track">
                    <div className="landing-mock-pvp-fill landing-mock-pvp-fill-opponent" style={{ width: '45%' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Elaborative encoding → Gold Card Builds & Playstyles.
              THE CARDS ARE THE GAME'S CARDS. Every row below is one entry of
              BUILD_ROWS (data/goldCardCatalog.js) and every card is the same
              <PillCard> the shop mounts on its shelf (PlayerShop.jsx:233-241) --
              same component, same face, same registry glyph, same accent, same
              canonical ledger chips on hover. Three props the shop passes are
              deliberately NOT passed: `owned`, `actionButton` and `requirement`
              are PLAYER STATE, and this page has no player. `showCost` is the
              framing choice this surface makes, exactly as the shop makes its
              own.

              Nothing about a drug is written here: no ability name, no drug
              class, no cost, no colour, no icon, no effect prose, and no list of
              which cards belong together. All of it is derived, so a new gold
              card, a fourth keystone or a newly authored pairing appears on this
              page with zero edits to this file.

              data-axis stays on the SECTION. Landing.css hangs ::before/::after
              off `.landing [data-axis]`, which would outrank `.pill-card::before`
              (the accent stripe) if it were ever put on a mounted card. */}
          <div className="landing-showcase-wide landing-animate" data-axis="understanding">
            <div className="landing-showcase-wide-header">
              <div>
                <span className="landing-method-tag">Elaborative encoding</span>
                <h3>Gold Card Builds & Playstyles</h3>
                <p>A fact you can reason from outlasts a fact you memorized. Unlock golden cards through achievements and progression milestones. Each card's combat effect is tied to its real pharmacology, so picking a build before a boss fight means thinking in mechanisms, not flashcard order.</p>
              </div>
            </div>
            <div className="landing-builds-list">
              {BUILD_ROWS.map((row) => (
                /* The row accent rides a custom property instead of a per-build
                   colour class, so the label is tinted by the SAME value the
                   cards are (the keystone's accent, or the lead card's skill-tree
                   node colour) and a new row cannot arrive unstyled. */
                <div className="landing-build-row" key={row.id} style={{ '--build-accent': row.accent }}>
                  <div className="landing-build-meta">
                    <span className="landing-build-label">{row.label}</span>
                    {/* PLAIN TEXT, and both of the more "faithful" renderings were
                        tried and measured before settling here:
                        - `keystoneBonus`, the prop that owns these lines in the
                          app, renders inside the hover panel. That panel is
                          visibility:hidden but still takes layout, so attaching it
                          grew every card to ~3x height with a dead band down the
                          middle of all ten.
                        - `renderStatusText`, the decorator PillCard runs them
                          through (PillCard.jsx:527), SUBSTITUTES GLYPHS FOR DRUG
                          NAMES: "under Vancomycin" renders as "under [sigma]". In
                          combat you know the marks; a first-time visitor reads
                          gibberish, and this is the page that meets them first.
                        Faithful to the game is the goal for the ASSETS. The prose
                        around them still has to be readable by someone who has
                        never played. */}
                    <span className="landing-build-synergy">
                      {row.lines.map((line, i) => (
                        <span className="landing-build-line" key={`${row.id}-${i}`}>{line}</span>
                      ))}
                    </span>
                  </div>
                  {/* showCost={false}, like every player-facing card in the app:
                      PlayerShop.jsx:237, KeystonePickModal.jsx:70,
                      LoadoutDraftModal.jsx:101, SkillTreeNodeDetailModal.jsx:230
                      and nodeDetailLayout.js:38/:57. The cost chip is admin
                      chrome (AdminReservoir.jsx:153 is its only consumer), and
                      leaving it on printed "Free" over five cards that are not
                      free: the three keystones are a starter CHOICE, and digoxin
                      and propranolol are unlocked by a criterion, not by price. */}
                  <div className="landing-build-cards">
                    {row.keystoneProps && <PillCard {...row.keystoneProps} showCost={false} />}
                    {row.cardProps.map((props) => (
                      <PillCard key={props.card.drug_slug} {...props} showCost={false} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Spaced repetition → Deck Maintenance & Upkeep */}
          <div className="landing-showcase landing-animate" data-axis="knowledge">
            <div className="landing-showcase-text">
              <span className="landing-method-tag">Spaced repetition</span>
              <h3>Deck Maintenance & Upkeep</h3>
              <p>Memory fades on a schedule, so review has to meet it there. Cards decay if you don't come back to them — a decay timer shows when each one drops a tier — and keeping your deck maintained keeps both your recall and your combat stats high.</p>
            </div>
            <div className="landing-showcase-visual">
              <div className="landing-mock-upkeep">
                <div className="landing-mock-upkeep-card">
                  <div className="landing-mock-upkeep-info">
                    <span className="landing-mock-upkeep-name">Lisinopril</span>
                    <span className="landing-mock-upkeep-status landing-mock-upkeep-status-warn">Needs review</span>
                  </div>
                  <div className="landing-mock-upkeep-bar">
                    <div className="landing-mock-upkeep-fill landing-mock-upkeep-fill-warn" style={{ width: '35%' }} />
                  </div>
                  <span className="landing-mock-upkeep-decay landing-mock-upkeep-decay-danger">
                    <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="8" r="6.5"/><path d="M8 4.5V8l2.5 1.5"/></svg>
                    2d
                  </span>
                </div>
                <div className="landing-mock-upkeep-card">
                  <div className="landing-mock-upkeep-info">
                    <span className="landing-mock-upkeep-name">Metformin</span>
                    <span className="landing-mock-upkeep-status landing-mock-upkeep-status-good">Strong</span>
                  </div>
                  <div className="landing-mock-upkeep-bar">
                    <div className="landing-mock-upkeep-fill landing-mock-upkeep-fill-good" style={{ width: '90%' }} />
                  </div>
                  <span className="landing-mock-upkeep-decay landing-mock-upkeep-decay-safe">
                    <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="8" r="6.5"/><path d="M8 4.5V8l2.5 1.5"/></svg>
                    12d
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Interleaving & structure → Skill Tree & Loadouts (full-width so the REAL tree has horizontal room) */}
          <div className="landing-showcase-wide landing-animate" data-axis="understanding">
            <div className="landing-showcase-wide-header">
              <div>
                <span className="landing-method-tag">Interleaving &amp; structure</span>
                <h3>Skill Tree &amp; Loadouts</h3>
                <p>Drugs stick as a map of neighbours, not as a list you recite in order. Unlock a branching tree of real drugs, equip a <strong>keystone</strong> for a global combat perk, then slot up to three <strong>drug cards</strong> (each with its own battle effect) into your loadout — every unlock puts a drug next to the ones it's easiest to confuse it with. Try it free on the map above, no signup.</p>
              </div>
            </div>
            <div className="landing-showcase-wide-visual landing-showcase-visual--skilltree">
              {/* The REAL skill tree — same SkillTreeViewport the dashboard/demo
                  renders, driven by the anon player-state. Read-only here (the
                  interactive one is the demo map above) so it's a faithful
                  preview, not a hand-drawn mock. Full-width for breathing room. */}
              <div className="landing-skilltree-embed" aria-label="Live skill tree preview">
                <SkillTreeViewport />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ LEVEL UP ============ */}
      <section className="landing-levelup">
        <div className="landing-section-inner">
          <p className="landing-section-label landing-animate">Level Up</p>
          <div className="landing-levelup-grid">
            {LEVELUP_ITEMS.map((item) => (
              <div key={item.label} className="landing-levelup-item landing-animate">
                <div className="landing-levelup-icon">
                  {item.icon}
                </div>
                <span className="landing-levelup-label">{item.label}</span>
                <span className="landing-levelup-desc">{item.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SOCIAL PROOF ============ */}
      <section className="landing-proof">
        <div className="landing-section-inner">
          <div className="landing-proof-grid landing-animate">
            {/* Live counters — fetched from /api/site-stats */}
            <div className="landing-proof-panel landing-proof-panel-live">
              <div className="landing-proof-panel-header">
                <span className="landing-proof-panel-dot landing-proof-panel-dot-live" />
                <span className="landing-proof-panel-title">Live</span>
              </div>
              <div className="landing-proof-stats">
                <div className="landing-proof-stat">
                  <span className="landing-proof-number">{liveStats.decks?.toLocaleString() ?? '--'}</span>
                  <span className="landing-proof-label">Decks Processed</span>
                </div>
                <div className="landing-proof-stat">
                  <span className="landing-proof-number">{liveStats.slides?.toLocaleString() ?? '--'}</span>
                  <span className="landing-proof-label">Slides Analyzed</span>
                </div>
                <div className="landing-proof-stat">
                  <span className="landing-proof-number">{liveStats.drugs?.toLocaleString() ?? '--'}</span>
                  <span className="landing-proof-label">Drug Cards</span>
                </div>
                <div className="landing-proof-stat">
                  <span className="landing-proof-number">{liveStats.boss_fights?.toLocaleString() ?? '--'}</span>
                  <span className="landing-proof-label">Boss Fights</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <MobileDesktopBanner
        page="landing"
        headline="Debono works best on desktop."
        subtext="Drop your email and we'll send you the link. Takes 2 seconds."
      />

      {/* ============ PRICING ============ */}
      <section className="landing-pricing">
        <div className="landing-section-inner">
          <p className="landing-section-label landing-animate">Pricing</p>
          {/* The headline asks; the line under it answers with the number.
              "70% cheaper than RxPrep" used to BE the headline — it is the proof,
              not the pitch, so it moved down rather than being duplicated. */}
          <h2 className="landing-pricing-title landing-animate">
            Why pay <em>more</em> for what you <em>enjoy less?</em>
          </h2>
          <p className="landing-pricing-sub landing-animate">
            70% cheaper than RxPrep. <strong>Start free.</strong>
          </p>
          <div className="landing-pricing-grid">
            <div className="landing-pricing-card landing-animate">
              <h3 className="landing-pricing-name">Free</h3>
              <div className="landing-pricing-price">$0</div>
              <ul className="landing-pricing-features">
                <li>15 deck uploads</li>
                <li>5M AI credits/month</li>
                <li>Boss fights, PvP, gear</li>
                <li>Full RPG progression</li>
              </ul>
              <Link to={user ? "/dashboard" : "/signup"} className="landing-pricing-btn landing-pricing-btn-free">{user ? "Go to Dashboard" : "Start Free"}</Link>
            </div>
            <div className="landing-pricing-card landing-animate">
              <h3 className="landing-pricing-name">Monthly</h3>
              <div className="landing-pricing-price">$15<span>/mo</span></div>
              <ul className="landing-pricing-features">
                <li>Unlimited deck uploads</li>
                <li>Unlimited AI credits</li>
                <li>No monthly ceiling</li>
                <li>Upload your whole curriculum</li>
              </ul>
              <Link to="/pricing" className="landing-pricing-btn landing-pricing-btn-premium">View Details</Link>
            </div>
            <div className="landing-pricing-card landing-pricing-card-highlight landing-animate">
              <div className="landing-pricing-badge">Best Value</div>
              <h3 className="landing-pricing-name">Annual</h3>
              <div className="landing-pricing-price">$120<span>/yr</span></div>
              <p className="landing-pricing-effective">$10/mo · save 33%</p>
              <ul className="landing-pricing-features">
                <li>Everything in Monthly</li>
                <li>NAPLEX Prep included free</li>
                <li>$10/mo, save 33%</li>
              </ul>
              <Link to="/pricing" className="landing-pricing-btn landing-pricing-btn-purple">View Details</Link>
            </div>
          </div>
          <div className="landing-pricing-dlc-row">
            <div className="landing-pricing-card landing-pricing-card-naplex landing-animate">
              <div className="landing-pricing-badge landing-pricing-badge-green">Permanent DLC</div>
              {fNaplex && (
                <div className="landing-founders-flag landing-founders-flag-naplex">
                  <span className="landing-founders-flag-dot" /> Founders · first {fNaplex.slots}
                </div>
              )}
              <h3 className="landing-pricing-name">NAPLEX DLC</h3>
              <div className="landing-pricing-price">
                {fNaplex && <span className="landing-pricing-price-was">${fNaplex.orig}</span>}
                ${fNaplex ? fNaplex.sale : 50}<span> once</span>
              </div>
              <ul className="landing-pricing-features">
                <li>15 NAPLEX therapeutic areas</li>
                <li>Unlimited AI-generated practice</li>
                <li>Works standalone or with Premium</li>
              </ul>
              <Link to="/naplex-prep" className="landing-pricing-btn landing-pricing-btn-naplex">View Details</Link>
            </div>
            <div className="landing-pricing-card landing-pricing-card-ptcb landing-animate">
              <div className="landing-pricing-badge landing-pricing-badge-ptcb">Permanent DLC</div>
              <h3 className="landing-pricing-name">PTCB DLC</h3>
              <div className="landing-pricing-price">$35<span> once</span></div>
              <ul className="landing-pricing-features">
                <li>4 PTCB exam domains</li>
                <li>Unlimited AI-generated practice</li>
                <li>Works standalone or with Premium</li>
              </ul>
              <Link to="/ptcb-prep" className="landing-pricing-btn landing-pricing-btn-ptcb">View Details</Link>
            </div>
            <div className="landing-pricing-card landing-pricing-card-mpje landing-animate">
              <div className="landing-pricing-badge landing-pricing-badge-mpje">Permanent DLC</div>
              {fMpje && (
                <div className="landing-founders-flag landing-founders-flag-mpje">
                  <span className="landing-founders-flag-dot" /> Founders · first {fMpje.slots}
                </div>
              )}
              <h3 className="landing-pricing-name">MPJE DLC</h3>
              <div className="landing-pricing-price">
                {fMpje && <span className="landing-pricing-price-was">${fMpje.orig}</span>}
                ${fMpje ? fMpje.sale : 35}<span> once</span>
              </div>
              <ul className="landing-pricing-features">
                <li>6 federal MPJE domains</li>
                <li>Citation-tracked pharmacy law drills</li>
                <li>Inspector boss fights + timeline scenarios</li>
              </ul>
              <Link to="/mpje-prep" className="landing-pricing-btn landing-pricing-btn-mpje">View Details</Link>
            </div>
          </div>
          <p className="landing-pricing-footnote landing-animate">
            All plans include AI quiz generation, drug card extraction, and spaced repetition from YOUR lecture slides.
            <br />
            <Link to="/pricing">Full pricing details and FAQ</Link>
          </p>
        </div>
      </section>

      {/* ============ BOTTOM CTA ============ */}
      <p className="landing-sentiment landing-animate">The playing is the preparation.</p>
      <section className="landing-bottom-cta landing-animate">
        <div className="landing-section-inner">
          {/* No promised results here: "ace every exam" / "you'll be more than prepared"
              read as a guarantee of outcomes we cannot guarantee. The theme is kept by
              naming the FEELING (dread, effort) rather than the grade. */}
          <h2 className="landing-bottom-title">
            Ready to <em>stop dreading exams?</em>
          </h2>
          <p className="landing-bottom-sub">
            Turn your lectures into a game and put the hours in without feeling them. Start with your own material, free.
          </p>
          <Link to={user ? "/dashboard" : "/signup"} className="landing-btn-primary" style={{ '--btn-bg': '#f43f6b' }}>{user ? "Go to Dashboard" : "Get Started Free"}</Link>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="landing-brand">
            <span className="landing-wordmark-sm">DEBONO</span>
          </div>
          <span className="landing-footer-text">
            Built for healthcare students, by healthcare people. The{' '}
            <a
              href="https://stokasoftware.com/blog/debono-1-0-first-step-toward-healthcare-mmo/"
              className="landing-footer-origin-link"
              target="_blank"
              rel="noopener"
            >
              first step toward a healthcare MMO
            </a>
            .
          </span>
          <div className="landing-footer-links">
            <Link to="/blog">Blog</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
